import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HeaderMenu, SubscriptionType } from '@app/enums/_index';
import { LMSSubscription, ResourcesMenuItem, User } from '@app/models/_index';
import { Observable, Subscription, map } from 'rxjs';

import { RESOURCES } from '@app/data/_index';
import { SubscriptionFactory } from '@app/factories/_index';
import { UserService } from '@app/services/_index';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-header-mobile',
  templateUrl: './header-mobile.component.html',
  styleUrls: ['./header-mobile.component.scss'],
  standalone: false,
})
export class HeaderMobileComponent implements OnInit, OnDestroy {
  showModalNav = false;
  isExpandedCatalog = false;
  isExpandedResources = false;
  isExpandedProducts = false;
  userDropDown = false;
  resourceArray = RESOURCES.resource; // Read in navigation resources as json array from resources.json file

  isAuthenticated$: Observable<boolean>;
  avatarUrl$: Observable<string>;

  sub: Subscription;
  doceboUrl = environment.docebo.url;
  subscriptionItems: LMSSubscription[] = [];

  @Output() logoutClick = new EventEmitter<boolean>();
  @Output() clearDataStorage = new EventEmitter<boolean>();
  @Input() messageInboxCount: number;

  constructor(public subscriptionFactory: SubscriptionFactory, private userService: UserService) {}

  ngOnInit(): void {
    this.subscriptionItems = this.subscriptionFactory.getCollection();

    this.isAuthenticated$ = this.userService.isAuthenticated$;
    this.avatarUrl$ = this.userService?.doceboUser$.pipe(map((user: User) => user.avatarUrl));
  }

  /**
   * On Toggle Navigation
   */
  onToggleNavigation(): void {
    this.showModalNav = !this.showModalNav;
  }

  /**
   * On Toggle Header Menu for mobile navigation
   * @param - menu item selected
   */
  onToggleDropdown(menu: string): void {
    switch (menu) {
      case HeaderMenu.PRODUCTS:
        this.isExpandedProducts = !this.isExpandedProducts;
        this.isExpandedCatalog = false;
        this.isExpandedResources = false;
        break;
      case HeaderMenu.CATALOG:
        this.isExpandedCatalog = !this.isExpandedCatalog;
        this.isExpandedProducts = false;
        this.isExpandedResources = false;
        break;
      case HeaderMenu.RESOURCES:
        this.isExpandedResources = !this.isExpandedResources;
        this.isExpandedProducts = false;
        this.isExpandedCatalog = false;
        break;
      default:
        break;
    }
  }

  /**
   * Emits the data to clear the Session Storage data
   */
  clearStorage(): void {
    this.clearDataStorage.emit(true);
  }

  /**
   * On Toggle User Menu for mobile navigation
   */
  onToggleUserDropdown(): void {
    this.userDropDown = !this.userDropDown;
  }

  /**
   * On Logout Click
   * Redirect to home page after log out
   */
  onLogoutClick(): void {
    this.logoutClick.emit(true);
    this.showModalNav = false;
  }

  /**
   * Add Track By Mobile Resource Name to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of resource array
   * @param item - Resource item
   * @returns resource name
   */
  trackByMobileResourceName(index: number, item: ResourcesMenuItem): string {
    if (item) {
      return 'mobile-' + item.name;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Subscription Item to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Subscription Item array
   * @param item - LMS Subscription item
   * @returns LMS Subscription id
   */
  trackBySubscriptionItem(index: number, item: LMSSubscription): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   * OnDestroy unsubscribe from auth service user
   */
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   *  HeaderMenu enums added.
   */
  public get headerMenu(): typeof HeaderMenu {
    return HeaderMenu;
  }

  /**
   * Subscription Type enums based on subscription types
   */
  public get subscriptionType(): typeof SubscriptionType {
    return SubscriptionType;
  }
}
